import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  CheckboxSize,
  Show
} from "shared/components";
import { AssigneeSearchInput } from "shared/components/Assignee/AssigneeSearchInput";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  CalendarMinus02Icon,
  Calendar01Icon,
  CalendarCheckOut02Icon,
  Calendar03Icon,
  FileMinusIcon,
  UserMinus01Icon,
  Cancel01Icon,
  Calendar04Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";
import moment from "moment";
import { DatePicker as DatePickerCalendar } from "shared/components/DatePickerCalendar";
import { Avatar } from "shared/components/Avatar";
import {
  DropdownLabels,
  DueDateLabels,
  RelatedMapping,
  DefaultOptionsMap,
  getDueDateLabel
} from "../constants";
import { DetailTabData, DropdownId } from "../types";
import { useAuthContext } from "providers/Auth";
import { User } from "views/settings-users/types";
import { api, RelatedObject } from "api/task";
import { useMemo, useState } from "react";
import { sortAssignees } from "shared/components/Assignee";
import { useQuery } from "@tanstack/react-query";
import { Card } from "shared/components/Card";
import { toast } from "react-toastify";
import { useMedia } from "shared/providers";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";

type DetailTabPopoversProps = {
  data: DetailTabData | null;
  onDataChange: (data: DetailTabData) => void;
  assignees: User[];
  readOnly?: boolean;
};

export const DetailTabPopovers = ({
  data,
  onDataChange,
  assignees,
  readOnly = false
}: DetailTabPopoversProps) => {
  const { auth } = useAuthContext();
  const { isMobile } = useMedia();
  const [relatedSearch, setRelatedSearch] = useState("");
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [debouncedAssigneeSearch] = useDebouncedValue(assigneeSearch, 500);
  const [open, setOpen] = useState<DropdownId | null>(null);
  const [assigneeListSnapshot, setAssigneeListSnapshot] = useState<User[]>([]);
  const toggleDropdown = (id: DropdownId) => {
    setOpen((prev) => {
      const nextOpen = prev === id ? null : id;
      if (nextOpen === "assignee") {
        const selectedIds = new Set(selectedAssignees.map((a) => a.id));
        const unselected = sortedAssignees.filter(
          (a) => !selectedIds.has(a.id)
        );
        setAssigneeListSnapshot([
          ...selectedAssignees,
          ...unselected
        ] as User[]);
      }
      return nextOpen;
    });
  };
  const closeAllDropdowns = () => setOpen(null);

  const { data: relatedObjectsData, isLoading: isRelatedLoading } = useQuery({
    queryKey: ["RELATED_OBJECTS", relatedSearch],
    queryFn: () => api.getRelatedObjects({ search: relatedSearch }),
    enabled: open === "related" || relatedSearch.trim().length > 0
  });

  const relatedItems = relatedObjectsData?.items ?? [];
  const selectedAssignees = data?.assignees ?? [];
  const dueLabel = getDueDateLabel(data?.due_date);
  const isDueDateSet = !!data?.due_date;
  const dueLabelColor =
    dueLabel === DueDateLabels.none ? "text-content-200" : "text-content-1000";
  const sortedAssignees = useMemo(
    () => sortAssignees(assignees, auth?.id),
    [assignees, auth]
  );

  const filteredAssignees = useMemo(() => {
    const baseList =
      open === "assignee" ? assigneeListSnapshot : sortedAssignees;
    if (!debouncedAssigneeSearch.trim()) return baseList;
    const searchTerm = debouncedAssigneeSearch.toLowerCase();
    return baseList.filter(
      (a) =>
        a.name.toLowerCase().includes(searchTerm) ||
        a.email.toLowerCase().includes(searchTerm)
    );
  }, [assigneeListSnapshot, sortedAssignees, debouncedAssigneeSearch, open]);

  const handleAssigneeChange = (newIds: number[]) => {
    const newIdSet = new Set(newIds);
    const newAssignees = (data?.assignees ?? assignees).filter((a) =>
      newIdSet.has(Number(a.id))
    );
    onDataChange({
      ...data,
      assignees: newAssignees
    } as DetailTabData);
  };

  const handleDueChange = (iso: string | null) => {
    onDataChange({
      ...data,
      due_date: iso
    } as DetailTabData);
    closeAllDropdowns();
  };

  const handleRelatedSearch = (search: string) => {
    setRelatedSearch(search);
  };

  const handleRelatedKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleRelatedSearch(relatedSearch);
  };

  const handleRelatedBlur = () => {
    if (relatedSearch.trim()) handleRelatedSearch(relatedSearch);
  };
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <div className="pt-2.5 min-w-[120px]">
          <span className="text-sm text-content-400">
            {DropdownLabels.assignee}
          </span>
        </div>

        <div className="relative flex-1">
          <Button
            variant={ButtonVariant.BASE}
            borderLess
            size={ButtonSize.SM}
            onClick={() => !readOnly && toggleDropdown("assignee")}
            disabled={readOnly}
            className={cx(
              "w-full border border-alpha-100 py-2.5 px-[14px] shadow-xs !justify-start",
              selectedAssignees.length > 1
                ? "!min-h-[83px] !max-h-[120px]"
                : "min-h-[47px]"
            )}
          >
            <Show if={selectedAssignees.length === 0}>
              <div className="flex items-center gap-3">
                <HugeiconsIcon
                  icon={UserMinus01Icon}
                  className="text-content-200"
                  size={20}
                />
                <span className="text-sm font-medium text-content-200">
                  {DefaultOptionsMap.none_assignee}
                </span>
              </div>
            </Show>
            <Show if={selectedAssignees.length > 0}>
              <div className="flex flex-wrap gap-2">
                {selectedAssignees.slice(0, 2).map((c) => (
                  <Badge
                    key={c.id}
                    className="shrink-0 items-center leading-none gap-2 !text-content-1000"
                    size={BadgeSize.LG}
                    variant={BadgeVariant.BASE}
                    leftElement={
                      <>
                        <Avatar
                          name={c.name}
                          className="text-[9px]"
                          size={20}
                        />
                        <p className="text-xs font-medium">{c.name}</p>
                      </>
                    }
                    rightElement={
                      <HugeiconsIcon
                        icon={Cancel01Icon}
                        size={12}
                        strokeWidth={1.2}
                      />
                    }
                    onClick={() => {
                      if (readOnly) return;
                      const newAssignees = selectedAssignees.filter(
                        (a) => a.id !== c.id
                      );
                      onDataChange({
                        ...data,
                        assignees: newAssignees
                      } as DetailTabData);
                    }}
                  />
                ))}
                {selectedAssignees.length > 2 && (
                  <Badge
                    className="shrink-0 items-center leading-none !text-content-1000"
                    size={BadgeSize.LG}
                    variant={BadgeVariant.BASE}
                  >
                    <p className="text-xs font-medium">
                      +{selectedAssignees.length - 2}
                    </p>
                  </Badge>
                )}
              </div>
            </Show>

            <HugeiconsIcon
              icon={ArrowDown01Icon}
              className={cx(
                "absolute right-3.5 text-content-1000",
                selectedAssignees.length > 1
                  ? "top-2.5"
                  : "top-1/2 -translate-y-1/2",
                open === "assignee" ? "rotate-180" : ""
              )}
              size={16}
              strokeWidth={1.5}
            />
          </Button>

          <Show if={open === "assignee"}>
            <div className="fixed inset-0 z-40" onClick={closeAllDropdowns} />
            <Card
              className={cx(
                "absolute top-[calc(100%+8px)] z-40 overflow-hidden w-full",
                isMobile ? "right-0 max-w-[280px]" : "max-w-[294px]"
              )}
            >
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAssigneeChange([]);
                  closeAllDropdowns();
                }}
                variant={ButtonVariant.BASE}
                borderLess
                className="w-full !justify-start !px-4 !py-2.5 !min-h-[51px] border-b border-alpha-40 !gap-3 !text-content-200 rounded-none"
                HugeIcon={UserMinus01Icon}
              >
                <span className="text-base font-medium text-content-200">
                  {DefaultOptionsMap.none_assignee}
                </span>
              </Button>

              <AssigneeSearchInput
                id="assignee-search"
                placeholder="Search assignee"
                value={assigneeSearch}
                onChange={setAssigneeSearch}
                autoFocus
              />

              <div className="max-h-[300px] overflow-y-auto px-4">
                <Show if={filteredAssignees.length === 0 && !!assigneeSearch}>
                  <div className="px-4 py-2.5 text-content-400 text-sm">
                    No users found
                  </div>
                </Show>
                {filteredAssignees.map((a, idx) => {
                  const isAssigned = !!selectedAssignees.some(
                    (assignee) => assignee.id === a.id
                  );
                  return (
                    <Checkbox
                      key={a.id}
                      onChange={(e) => {
                        e.stopPropagation();
                        const newAssignees = selectedAssignees.some(
                          (u) => u.id === a.id
                        )
                          ? selectedAssignees.filter((u) => u.id !== a.id)
                          : [...selectedAssignees, a];
                        onDataChange({
                          ...data,
                          assignees: newAssignees
                        } as DetailTabData);
                      }}
                      checked={isAssigned}
                      size={CheckboxSize.SM}
                      wrapperClassName={cx(
                        "px-4 py-2.5 gap-3",
                        idx !== filteredAssignees.length - 1
                          ? "border-b border-alpha-40"
                          : ""
                      )}
                      label={
                        <div className="flex items-center gap-3">
                          <Avatar
                            name={a.name}
                            className="text-[9px] font-black"
                            size={22}
                          />
                          <p className="flex items-center text-content-1000 text-base font-medium">
                            {a.name}
                            <Show if={a.id === auth?.id}>
                              <p className="text-content-200">&nbsp;(You)</p>
                            </Show>
                          </p>
                        </div>
                      }
                    />
                  );
                })}
              </div>
            </Card>
          </Show>
        </div>
      </div>

      <div className="flex items-start gap-2 max-h-[40px]">
        <div className="pt-2.5 min-w-[120px]">
          <span className="text-sm text-content-400">{DropdownLabels.due}</span>
        </div>
        <div className="relative flex-1">
          <Button
            variant={ButtonVariant.BASE}
            borderLess
            size={ButtonSize.SM}
            onClick={() => !readOnly && toggleDropdown("due")}
            disabled={readOnly}
            className="w-full border border-alpha-100 py-2.5 px-[14px] shadow-xs !justify-start"
          >
            <div className="flex items-center gap-3">
              <HugeiconsIcon
                icon={isDueDateSet ? Calendar04Icon : CalendarMinus02Icon}
                className={
                  isDueDateSet ? "text-content-1000" : "text-content-200"
                }
                size={20}
              />
              <span className={cx("text-sm font-medium", dueLabelColor)}>
                {dueLabel}
              </span>
            </div>
            <HugeiconsIcon
              icon={ArrowDown01Icon}
              className={cx(
                "absolute right-3 top-1/2 -translate-y-1/2 text-content-1000",
                open === "due" ? "rotate-180" : null
              )}
              size={16}
              stroke="1.5px"
            />
          </Button>

          <Show if={open === "due"}>
            <Card
              className={cx(
                "absolute top-[calc(100%+8px)] z-[50] overflow-hidden w-full",
                isMobile ? "right-0 max-w-[200px]" : "max-w-[240px]"
              )}
              onClick={closeAllDropdowns}
            >
              <Button
                onClick={() => handleDueChange("")}
                variant={ButtonVariant.BASE}
                borderLess
                className="flex w-full items-center !justify-start !px-4 !py-2.5 !min-h-[51px] !gap-3 !rounded-none !bg-transparent !text-content-200 !stroke-content-200 !border-b !border-alpha-40 hover:!bg-alpha-40"
                HugeIcon={CalendarMinus02Icon}
              >
                <span className="text-base font-medium">
                  {DueDateLabels.none}
                </span>
              </Button>
              <Button
                onClick={() =>
                  handleDueChange(moment.utc().startOf("day").toISOString())
                }
                variant={ButtonVariant.BASE}
                borderLess
                className="flex w-full items-center !justify-start !px-4 !py-2.5 !min-h-[51px] !gap-3 !rounded-none !bg-transparent !text-content-1000 !stroke-content-1000 hover:!bg-alpha-40 !border-b !border-alpha-40"
                HugeIcon={Calendar01Icon}
              >
                <span className="text-base font-medium">
                  {DueDateLabels.today}
                </span>
              </Button>

              <Button
                onClick={() =>
                  handleDueChange(
                    moment.utc().add(1, "day").startOf("day").toISOString()
                  )
                }
                variant={ButtonVariant.BASE}
                borderLess
                className="flex w-full items-center !justify-start !px-4 !py-2.5 !min-h-[51px] !gap-3 !rounded-none !bg-transparent !text-content-1000 !stroke-content-1000 hover:!bg-alpha-40 !border-b !border-alpha-40"
                HugeIcon={CalendarCheckOut02Icon}
              >
                <span className="text-base font-medium">
                  {DueDateLabels.tomorrow}
                </span>
              </Button>

              <Button
                onClick={() =>
                  handleDueChange(
                    moment.utc().add(1, "week").startOf("day").toISOString()
                  )
                }
                variant={ButtonVariant.BASE}
                borderLess
                className="flex w-full items-center !justify-start !px-4 !py-2.5 !min-h-[51px] !gap-3 !rounded-none !bg-transparent !text-content-1000 !stroke-content-1000 hover:!bg-alpha-40 !border-b !border-alpha-40"
                HugeIcon={CalendarCheckOut02Icon}
              >
                <span className="text-base font-medium">
                  {DueDateLabels.next_week}
                </span>
              </Button>

              <Button
                onClick={() => toggleDropdown("datePicker")}
                variant={ButtonVariant.BASE}
                borderLess
                className="flex w-full items-center !justify-start !px-4 !py-2.5 !min-h-[51px] !gap-3 !rounded-none !bg-transparent !text-content-1000 !stroke-content-1000 hover:!bg-alpha-40 !border-b !border-alpha-40"
                HugeIcon={Calendar03Icon}
              >
                <span className="text-base font-medium">
                  {DueDateLabels.custom}
                </span>
              </Button>
            </Card>
          </Show>
          <Show if={open === "datePicker"}>
            <div className="fixed inset-0 z-[45]" onClick={closeAllDropdowns} />
            <div
              className={cx(
                "absolute top-[calc(100%+8px)] z-[50] rounded-lg border border-alpha-100 bg-white shadow-lg",
                isMobile ? "right-0" : "w-full"
              )}
            >
              <DatePickerCalendar
                selectedDate={data?.due_date}
                onSelectDate={(ymd) => {
                  const selectedDate = moment
                    .utc(ymd, "YYYY-MM-DD")
                    .startOf("day");
                  const today = moment.utc().startOf("day");

                  if (selectedDate.isBefore(today)) {
                    toast.error("Cannot select a date in the past");
                    return;
                  }

                  const iso = selectedDate.toISOString();
                  handleDueChange(iso);
                }}
              />
            </div>
          </Show>
        </div>
      </div>

      <div className="flex items-start gap-2">
        <div className="pt-2.5 min-w-[120px]">
          <span className="text-sm text-content-400">
            {DropdownLabels.related}
          </span>
        </div>
        <div className="relative flex-1">
          <Button
            variant={ButtonVariant.BASE}
            borderLess
            size={ButtonSize.SM}
            onClick={() => !readOnly && toggleDropdown("related")}
            disabled={readOnly}
            className={cx(
              "w-full border border-alpha-100 py-2.5 px-[14px] shadow-xs !justify-start",
              data?.subject ? "min-h-[59px]" : "min-h-[39px]"
            )}
          >
            {data?.subject ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs font-medium text-content-1000">
                  {RelatedMapping[data.subject.type]}
                  <span className="h-[6px] w-[6px] rounded-full bg-alpha-100" />
                  {data.subject.property.name}
                </div>
                <Show if={data.subject.name}>
                  <div className="text-left text-xs text-content-400 truncate font-normal">
                    {data.subject.name}
                  </div>
                </Show>
                <Show if={!data.subject.name}>
                  <div className="h-[18px]" />
                </Show>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <HugeiconsIcon
                  icon={FileMinusIcon}
                  className="text-content-200"
                  size={20}
                  strokeWidth={1.5}
                />
                <span className="text-sm font-medium text-content-200">
                  {DefaultOptionsMap.none_related_object}
                </span>
              </div>
            )}

            <HugeiconsIcon
              icon={ArrowDown01Icon}
              className={cx(
                "absolute right-3 top-1/2 -translate-y-1/2 text-content-1000",
                open === "related" ? "rotate-180" : ""
              )}
              size={16}
              stroke="1.5px"
            />
          </Button>

          <Show if={open === "related"}>
            <Card
              className={cx(
                "absolute top-[calc(100%+8px)] z-40 max-h-[500px] overflow-y-auto",
                isMobile
                  ? "right-0 w-[350px]"
                  : "w-[413px] translate-x-1 right-1"
              )}
              onClick={closeAllDropdowns}
            >
              <Button
                onClick={() => {
                  onDataChange({
                    ...data,
                    subject: null
                  } as any);
                  closeAllDropdowns();
                }}
                variant={ButtonVariant.BASE}
                borderLess
                className="w-full !justify-start !gap-3 !min-h-[51px] !border-b border-alpha-40 !px-4 !py-2.5 !text-content-200 !rounded-b-none !rounded-t-lg"
                HugeIcon={FileMinusIcon}
              >
                <div className="text-base font-medium">
                  {DefaultOptionsMap.none_related_object}
                </div>
              </Button>

              <AssigneeSearchInput
                id="related-search"
                placeholder="Search"
                value={relatedSearch}
                onChange={handleRelatedSearch}
                onKeyDown={handleRelatedKeyDown}
                onBlur={handleRelatedBlur}
                autoFocus
              />
              <Show if={isRelatedLoading}>
                <div className="px-4 py-2 text-sm text-content-300">
                  Loading…
                </div>
              </Show>
              {relatedItems.map((it: RelatedObject, idx) => {
                const typeText = RelatedMapping[it.type];
                const propertyName = it.property?.name;
                const sub = it.name?.trim();
                return (
                  <Button
                    key={`${it.type}:${it.id}-${idx}`}
                    onClick={() => {
                      onDataChange({
                        ...data,
                        subject: {
                          id: Number(it.id),
                          name: it.name,
                          type: it.type,
                          property: it.property
                        }
                      } as DetailTabData);
                      closeAllDropdowns();
                    }}
                    variant={ButtonVariant.BASE}
                    borderLess
                    className={cx(
                      "w-full !justify-start !gap-3 !px-4 !py-3 !min-h-[72px] rounded-none",
                      idx !== relatedItems.length - 1
                        ? "!border-b !border-alpha-40"
                        : ""
                    )}
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-base font-medium text-content-1000">
                        <span>{typeText}</span>
                        <span className="h-[6px] w-[6px] rounded-full bg-alpha-100" />
                        <span>{propertyName}</span>
                      </div>
                      <Show if={sub}>
                        <div className="text-left text-sm text-content-400 font-normal">
                          {sub}
                        </div>
                      </Show>
                      <Show if={!sub}>
                        <div className="h-[18px]" />
                      </Show>
                    </div>
                  </Button>
                );
              })}
            </Card>
          </Show>
        </div>
      </div>
    </div>
  );
};
